import { usePutFrontRichiesteIdModuliId } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { ConfirmUpdateLogic } from '@framework/common-web';
import { serializeFormSectionsValues } from '@framework/engine';
import { useTranslation } from '@framework/i18n';
import { ObjectSchema } from '@framework/validation';
import { message } from 'antd';
import { useCallback } from 'react';
import { useSubmissionConfirmUpdate } from '../form/useSubmissionConfirmUpdate';

/**
 * Hook che ritorna la funzione di "submit" per il salvataggio
 * in bozza di un modulo.
 */
export function useDraftSaveModuleForm(params: {
  submissionModuleId: string | null | undefined;
  submissionId: string | null | undefined;
  validationSchema: ObjectSchema<any>;
  onSuccess?: () => Promise<void>;
  onBeforeSave?: () => Promise<void>;
  onAfterSave?: (isValid: boolean) => Promise<void>;
}) {
  const {
    validationSchema,
    submissionModuleId,
    submissionId,
    onSuccess,
    onAfterSave,
    onBeforeSave,
  } = params;

  const confirmUpdate = useSubmissionConfirmUpdate();

  const save = usePutFrontRichiesteIdModuliId({
    mutation: {
      onSuccess,
    },
  });
  const { t } = useTranslation();

  const forceSave = usePutFrontRichiesteIdModuliId({
    request: { headers: { forceSave: true } },
    mutation: {
      onSuccess,
    },
  });

  const saveForm = useCallback(
    async (params: {
      submissionId: string | null | undefined;
      submissionModuleId: string | null | undefined;
      values: any;
      force: boolean;
    }) => {
      const { submissionId, submissionModuleId, values, force } = params;

      if (!submissionModuleId) {
        console.error(`[SubmitModuleForm] Impossibile salvare il modulo: id modulo o id richiesta mancanti`, { submissionModuleId }); // prettier-ignore
        message.error(t("error-submit-module-form.errore-salvataggio-modulo", 'Errore durante il salvataggio, ID Modulo non presente')); // prettier-ignore
        return;
      }

      if (!submissionId) {
        console.error(`[SubmitModuleForm] Impossibile salvare il modulo: id modulo o id richiesta mancanti`, { submissionId }); // prettier-ignore
        message.error(t("error-submit-module-form.errore-salvataggio-richiesta", 'Errore durante il salvataggio, ID Richiesta non presente')); // prettier-ignore
        return;
      }

      // Controllo lo stato del form per capire se è valido o meno
      // ma non lancio eccezioni in caso di errore
      const isValid = await validationSchema.isValid(values);

      const saveFn = force ? forceSave : save;
      await onBeforeSave?.();

      await saveFn.mutateAsync({
        idRichiesta: submissionId,
        idRichiestaModulo: submissionModuleId,
        data: {
          statoValidazione: {
            valido: isValid,
            visitato: true,
            completato: isValid,
          },
          valoriSezioni: serializeFormSectionsValues(validationSchema, values),
        },
      });
      await onAfterSave?.(isValid);
    },
    [forceSave, onAfterSave, onBeforeSave, save, t, validationSchema],
  );

  const onSubmit = useCallback(
    async (values: any) => {
      console.log(`[SubmitModuleForm] Salvtaggio dati`, { values });
      try {
        await saveForm({
          submissionId,
          submissionModuleId,
          values,
          force: false,
        });
      } catch (e: any) {
        if (ConfirmUpdateLogic.needUserConfirm(e)) {
          confirmUpdate({
            differences: e.response.data,
            onOk: async () => {
              await saveForm({
                submissionId,
                submissionModuleId,
                values,
                force: true,
              });
            },
          });
          return;
        }

        console.error(`[SubmitModuleForm] Errore durante il salvataggio`, {error: e}); // prettier-ignore
        message.error(getNetworkErrorMessage(e, t("error-submit-module-form.error-saving",'Errore durante il salvataggio'))); // prettier-ignore
      }
    },
    [saveForm, submissionId, submissionModuleId, t, confirmUpdate],
  );

  return onSubmit;
}

export type IUseDraftSaveModuleForm = ReturnType<typeof useDraftSaveModuleForm>;
